import { isEmpty } from 'lodash';
import { ReactNode, useEffect, useState } from 'react';
import { View as RNView, TouchableOpacity } from 'react-native';
import { Text } from '@shared/components/Text';
import { ScrollView, View } from '@shared/components/View';
import { FilterTagOption } from '@shared/components/filter/types';
import { MBIconV2 } from '@shared/components/image';
import { MBTextInput } from '@shared/components/textInput';
import { Colors, grid } from '@shared/styles';
import { i18n } from '@shared/util/i18n';

interface MobileMultiSelectProps {
  options: FilterTagOption[];
  selectedOptions?: FilterTagOption[];
  onChange(options: FilterTagOption[]): void;
  onSearch?(keyword: string): void;
  onDeselectAll(): void;
  renderItem: (props: { data: FilterTagOption }) => ReactNode;
  paddingTop?: number;
  paddingHorizontal?: number;
  isMultiSelect?: boolean;
}

interface OptionsListItemProps {
  item: FilterTagOption;
  renderItem: (props: { data: FilterTagOption }) => ReactNode;
  isSelected?: boolean;
  showCheckbox?: boolean;
  onChange: () => void;
}

function OptionsListItem({
  item,
  renderItem,
  isSelected = false,
  showCheckbox = true,
  onChange,
}: OptionsListItemProps) {
  return (
    <TouchableOpacity
      onPress={onChange}
      key={item.value}
      style={{
        alignSelf: 'flex-start',
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-between',
        paddingVertical: grid(1.2),
        width: '100%',
      }}
    >
      <View>{renderItem({ data: item })}</View>
      {showCheckbox && (
        <View>
          <MBIconV2
            name={isSelected ? 'check-box' : 'check-box-empty'}
            color={Colors.v2.green}
            size={grid(5)}
          />
        </View>
      )}
    </TouchableOpacity>
  );
}

export function MobileMultiSelect({
  onChange,
  onSearch,
  onDeselectAll,
  options,
  selectedOptions,
  renderItem,
  paddingTop = grid(2),
  paddingHorizontal = grid(2),
  isMultiSelect = true,
}: MobileMultiSelectProps) {
  const [keyword, setKeyword] = useState('');
  const [keywordFilteredOptions, setKeywordFilteredOptions] = useState(options);
  const [keywordFilteredSelections, setKeywordFilteredSelections] = useState<
    FilterTagOption[] | undefined
  >(selectedOptions);
  const selectionsList = keywordFilteredSelections;
  const optionsList = (onSearch ? options : keywordFilteredOptions).filter(
    o => !selectionsList?.find(s => s.value === o.value),
  );

  useEffect(() => {
    setKeywordFilteredSelections(
      selectedOptions?.filter(option => option.label.toLowerCase().includes(keyword.toLowerCase())),
    );
    setKeywordFilteredOptions(
      options?.filter(option => option.label.toLowerCase().includes(keyword.toLowerCase())),
    );
  }, [keyword, selectedOptions, options]);

  const isFiltered = !isEmpty(keyword);

  return (
    <View>
      <RNView style={[{ paddingTop, paddingBottom: grid(1), paddingHorizontal }]}>
        <MBTextInput
          autoFocus
          placeholder={i18n('common.searchDotDotDot')}
          onChangeText={text => {
            setKeyword(text);

            if (onSearch) {
              onSearch(text);
            }
          }}
        />
      </RNView>
      <ScrollView paddingVertical={2} paddingHorizontal={2}>
        {!isFiltered && !isEmpty(selectionsList) && (
          <View
            row
            centerVertically
            style={{ justifyContent: 'space-between' }}
            paddingLeft={1}
            marginBottom={1}
          >
            <View>
              <Text size="body2">
                <Text size="body2" weight="semibold">
                  {selectionsList?.length ?? 0}{' '}
                </Text>
                Selected
              </Text>
            </View>
            <View>
              <TouchableOpacity
                onPress={onDeselectAll}
                style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}
              >
                <Text size="body2">Deselect All</Text>
                <View marginLeft={1}>
                  <MBIconV2 name="check-box" color={Colors.v2.green} size={grid(5)} />
                </View>
              </TouchableOpacity>
            </View>
          </View>
        )}
        {selectionsList?.map(item => (
          <OptionsListItem
            key={item.value}
            isSelected
            onChange={() => {
              if (isMultiSelect) {
                onChange(selectedOptions?.filter(o => o.value !== item.value) ?? []);
              } else {
                onChange([]);
              }
            }}
            item={item}
            renderItem={renderItem}
          />
        ))}

        {!isFiltered && !isEmpty(optionsList) && (
          <View marginVertical={1} paddingHorizontal={1}>
            <Text size="overline">Filter By</Text>
          </View>
        )}

        {optionsList.map(item => (
          <OptionsListItem
            key={item.value}
            onChange={() => {
              if (isMultiSelect) {
                onChange([...(selectedOptions || []), item]);
              } else {
                onChange([item]);
              }
            }}
            item={item}
            renderItem={renderItem}
          />
        ))}
      </ScrollView>
    </View>
  );
}
